import React from 'react';
import Premium3DHardwareSelector from './Premium3DHardwareSelector';
import { reportError } from '../utils/errorHandling';

function PremiumDonationPage({ user, onBackClick }) {
    try {
        // SEO Enhancement: Add comprehensive meta tags and structured data
        React.useEffect(() => {
            // Update page title and meta description for better SEO
            const originalTitle = document.title;
            document.title = "Donate Tech Hardware | Bridge Digital Divide | Free Computer Donations UK";
            
            // Create or update meta description
            let metaDescription = document.querySelector('meta[name="description"]');
            if (!metaDescription) {
                metaDescription = document.createElement('meta');
                metaDescription.name = 'description';
                document.head.appendChild(metaDescription);
            }
            metaDescription.content = "Donate your old computers, laptops, tablets and tech hardware to help bridge the digital divide. Free collection across UK. Transform lives through technology access.";
            
            // Add Open Graph tags for social sharing
            const ogTags = [
                { property: 'og:title', content: 'Donate Tech Hardware | Bridge Digital Divide' },
                { property: 'og:description', content: 'Transform lives by donating your unused tech hardware. Free collection, professional refurbishment, maximum impact.' },
                { property: 'og:type', content: 'website' },
                { property: 'og:image', content: '/images/donation-impact.jpg' },
                { name: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', content: 'Donate Tech Hardware | Bridge Digital Divide' },
                { name: 'twitter:description', content: 'Transform lives by donating your unused tech hardware. Free collection, professional refurbishment, maximum impact.' }
            ];
            
            ogTags.forEach(tag => {
                let existingTag = document.querySelector(`meta[${tag.property ? 'property' : 'name'}="${tag.property || tag.name}"]`);
                if (!existingTag) {
                    existingTag = document.createElement('meta');
                    if (tag.property) existingTag.setAttribute('property', tag.property);
                    if (tag.name) existingTag.setAttribute('name', tag.name);
                    document.head.appendChild(existingTag);
                }
                existingTag.content = tag.content;
            });
              // Add comprehensive structured data for search engines
            const structuredData = {
                "@context": "https://schema.org",
                "@graph": [
                    {
                        "@type": "DonateAction",
                        "agent": {
                            "@type": "Organization",
                            "name": "Digital Bridge Initiative",
                            "description": "Bridging the digital divide through technology donations",
                            "url": window.location.origin,
                            "sameAs": [
                                "https://twitter.com/digitalbridgeuk",
                                "https://linkedin.com/company/digital-bridge-initiative"
                            ]
                        },
                        "object": {
                            "@type": "Product",
                            "name": "Computer Hardware Donation",
                            "description": "Donate laptops, desktops, tablets, smartphones and computer components to bridge digital divide",
                            "category": "Technology Hardware",
                            "keywords": "computer donation, laptop donation, hardware recycling, digital divide, tech donation UK"
                        },
                        "recipient": {
                            "@type": "Organization",
                            "name": "Communities in Need",
                            "description": "Underserved communities and educational institutions lacking technology access"
                        },
                        "result": {
                            "@type": "Thing",
                            "name": "Digital Access & Environmental Impact",
                            "description": "Providing technology access for education while preventing electronic waste"
                        }
                    },
                    {
                        "@type": "Service",
                        "name": "Free Computer Hardware Collection UK",
                        "description": "Free collection service for donated computers, laptops, tablets and IT equipment across the United Kingdom",
                        "provider": {
                            "@type": "Organization",
                            "name": "Digital Bridge Initiative"
                        },
                        "areaServed": {
                            "@type": "Country",
                            "name": "United Kingdom"
                        },
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "GBP",
                            "description": "Free collection and processing of donated IT equipment"
                        }
                    },
                    {
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "What types of computer hardware can I donate?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "We accept laptops, desktop computers, tablets, smartphones, servers, and computer components. All donated hardware is professionally refurbished and redistributed to communities in need."
                                }
                            },
                            {
                                "@type": "Question", 
                                "name": "Is computer hardware collection free in the UK?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes, we provide completely free collection of donated computer hardware across the United Kingdom. Our service includes secure data destruction and professional refurbishment."
                                }
                            }
                        ]
                    }
                ]
            };
            
            let structuredDataScript = document.querySelector('#donation-structured-data');
            if (!structuredDataScript) {
                structuredDataScript = document.createElement('script');
                structuredDataScript.id = 'donation-structured-data';
                structuredDataScript.type = 'application/ld+json';
                document.head.appendChild(structuredDataScript);
            }
            structuredDataScript.textContent = JSON.stringify(structuredData);
            
            // Cleanup function to restore original title
            return () => {
                document.title = originalTitle;
            };
        }, []);

        const [selectedHardware, setSelectedHardware] = React.useState([]);
        const [formData, setFormData] = React.useState({
            name: user?.name || '',
            email: user?.email || '',
            phone: '',
            organization: '',
            address: '',
            city: '',
            postalCode: '',
            country: 'United Kingdom',
            pickupDate: '',
            notes: ''
        });
        const [isSubmitting, setIsSubmitting] = React.useState(false);
        const [submitStatus, setSubmitStatus] = React.useState(null);
        const [errorMessage, setErrorMessage] = React.useState('');

        const handleHardwareSelect = (hardware) => {
            const exists = selectedHardware.find(h => h.type === hardware.title);
            if (exists) {
                setSelectedHardware(prev => prev.filter(h => h.type !== hardware.title));
            } else {
                setSelectedHardware(prev => [...prev, {
                    type: hardware.title,
                    quantity: 1,
                    co2Impact: hardware.co2Impact
                }]);
            }
        };

        const handleQuantityChange = (type, quantity) => {
            setSelectedHardware(prev =>
                prev.map(h => h.type === type ? { ...h, quantity: parseInt(quantity) } : h)
            );
        };

        const handleInputChange = (e) => {
            const { name, value } = e.target;
            setFormData(prev => ({ ...prev, [name]: value }));
        };

        const calculateTotalImpact = () => {
            return selectedHardware.reduce((sum, item) => sum + (item.co2Impact * item.quantity), 0);
        };        const handleSubmit = async (e) => {
            e.preventDefault();
            setIsSubmitting(true);
            setErrorMessage('');

            try {
                const donationData = {
                    ...formData,
                    hardware: selectedHardware,
                    co2_saved: calculateTotalImpact(),
                    submission_date: new Date().toISOString()
                };

                // Use the existing donation service instead of direct API call
                if (!window.donationService) {
                    throw new Error('Donation service not available. Please refresh the page and try again.');
                }

                const result = await window.donationService.createDonation(donationData);

                if (result && result.success !== false) {
                    setSubmitStatus('success');
                    // Track conversion
                    if (window.gtag) {
                        window.gtag('event', 'donation_submitted', {
                            event_category: 'donation',
                            value: selectedHardware.length,
                            hardware_count: selectedHardware.length,
                            co2_saved: calculateTotalImpact()
                        });
                    }
                } else {
                    throw new Error(result?.error || 'Failed to submit donation. Please try again.');
                }
            } catch (error) {
                console.error('Donation submission error:', error);
                setSubmitStatus('error');
                setErrorMessage(error.message || 'There was an error submitting your donation. Please try again.');
            } finally {
                setIsSubmitting(false);
            }
        };

        if (submitStatus === 'success') {
            return (
                <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
                    <div className="container mx-auto px-4 py-16">
                        <div className="max-w-2xl mx-auto text-center">
                            <div className="success-animation mb-8">
                                <div className="w-24 h-24 mx-auto bg-green-500 rounded-full flex items-center justify-center mb-6 animate-pulse">
                                    <i className="fas fa-check text-4xl text-white"></i>
                                </div>
                                <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                                    Thank You for Your Donation!
                                </h1>
                                <p className="text-xl text-gray-300 mb-8">
                                    Your hardware will help bridge the digital divide and create learning opportunities worldwide.
                                </p>
                                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700">
                                    <h3 className="text-lg font-semibold mb-4">Impact Summary</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                                        <div>
                                            <div className="text-2xl font-bold text-green-400">{selectedHardware.length}</div>
                                            <div className="text-gray-400">Devices Donated</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-blue-400">{calculateTotalImpact()}kg</div>
                                            <div className="text-gray-400">CO₂ Prevented</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-purple-400">∞</div>
                                            <div className="text-gray-400">Lives Impacted</div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="mt-8 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105"
                                >
                                    Donate More Hardware
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
                {/* Animated Background */}
                <div className="absolute inset-0 opacity-30">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
                </div>

                <div className="relative z-10 container mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-xl rounded-full px-6 py-2 border border-white/20 mb-6">
                            <i className="fas fa-heart text-red-400 mr-2 animate-pulse"></i>
                            <span className="text-sm font-medium">Transform Lives Through Technology</span>
                        </div>
                          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                            <span className="bg-gradient-to-r from-white via-blue-200 to-green-200 bg-clip-text text-transparent">
                                Donate Computer
                            </span>
                            <br />
                            <span className="bg-gradient-to-r from-green-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
                                Hardware UK
                            </span>
                        </h1>
                          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            Free computer donation collection across the UK. Bridge the digital divide by donating laptops, desktops, tablets and tech hardware. 
                            Professional refurbishment • Secure data wiping • Maximum educational impact.
                        </p>
                    </div>

                    {/* Trust Signals Bar */}
                    <div className="trust-signals-bar bg-white/5 backdrop-blur-xl border-y border-white/10 py-4 mb-12">
                        <div className="container mx-auto px-4">
                            <div className="flex flex-wrap justify-center items-center gap-8 text-sm">
                                <div className="flex items-center space-x-2">
                                    <i className="fas fa-shield-alt text-green-400"></i>
                                    <span className="text-gray-300">ICO Registered</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <i className="fas fa-lock text-green-400"></i>
                                    <span className="text-gray-300">100% Secure Data Destruction</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <i className="fas fa-truck text-green-400"></i>
                                    <span className="text-gray-300">Free Collection Nationwide</span>
                                </div>                                <div className="flex items-center space-x-2">
                                    <i className="fas fa-certificate text-green-400"></i>
                                    <span className="text-gray-300">Certified Recycling</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Limited Time Offer Banner */}
                    <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur-xl rounded-2xl p-4 mb-8 border border-orange-500/30 max-w-4xl mx-auto">
                        <div className="flex items-center justify-center space-x-3">
                            <i className="fas fa-fire text-orange-400 text-xl animate-pulse"></i>
                            <p className="text-white font-medium">
                                <span className="text-orange-400">Limited Time:</span> Free same-day collection available for the next 
                                <span className="font-bold text-orange-400 ml-1">24 hours</span>
                            </p>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Hardware Selection - Takes up 2 columns */}
                            <div className="lg:col-span-2">
                                <div className="bg-black/40 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
                                    <h2 className="text-2xl font-bold mb-6 text-center">
                                        <span className="bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent">
                                            Select Hardware to Donate
                                        </span>
                                    </h2>
                                    
                                    <Premium3DHardwareSelector
                                        selectedHardware={selectedHardware}
                                        onHardwareSelect={handleHardwareSelect}
                                        onQuantityChange={handleQuantityChange}
                                    />

                                    {/* Impact Display */}
                                    {selectedHardware.length > 0 && (
                                        <div className="mt-8 bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-xl rounded-2xl p-6 border border-green-400/30">
                                            <h3 className="text-lg font-semibold mb-4 text-center">Your Impact</h3>
                                            <div className="grid grid-cols-3 gap-4 text-center">
                                                <div>
                                                    <div className="text-2xl font-bold text-green-400">{selectedHardware.length}</div>
                                                    <div className="text-gray-400 text-sm">Devices</div>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-blue-400">{calculateTotalImpact()}</div>
                                                    <div className="text-gray-400 text-sm">kg CO₂ Saved</div>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-purple-400">{Math.round(calculateTotalImpact() * 2.5)}</div>
                                                    <div className="text-gray-400 text-sm">Miles Not Driven</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Donation Form */}
                            <div className="lg:col-span-1">
                                <div className="bg-black/40 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl h-fit sticky top-8">
                                    <h2 className="text-2xl font-bold mb-6 text-center">
                                        <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                                            Your Details
                                        </span>
                                    </h2>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                                                    placeholder="Your full name"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                                                    placeholder="your@email.com"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                                                placeholder="Your phone number"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Organization</label>
                                            <input
                                                type="text"
                                                name="organization"
                                                value={formData.organization}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                                                placeholder="Company or organization"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Address *</label>
                                            <input
                                                type="text"
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                                                placeholder="Pickup address"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">City *</label>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                                                    placeholder="City"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">Postal Code *</label>
                                                <input
                                                    type="text"
                                                    name="postalCode"
                                                    value={formData.postalCode}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                                                    placeholder="Postal code"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Preferred Pickup Date</label>
                                            <input
                                                type="date"
                                                name="pickupDate"
                                                value={formData.pickupDate}
                                                onChange={handleInputChange}
                                                min={new Date().toISOString().split('T')[0]}
                                                className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl text-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Additional Notes</label>
                                            <textarea
                                                name="notes"
                                                value={formData.notes}
                                                onChange={handleInputChange}
                                                rows="3"
                                                className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all resize-none"
                                                placeholder="Any special instructions or notes..."
                                            />
                                        </div>

                                        {errorMessage && (
                                            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4">
                                                <div className="flex items-center text-red-400">
                                                    <i className="fas fa-exclamation-triangle mr-2"></i>
                                                    {errorMessage}
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={isSubmitting || selectedHardware.length === 0}
                                            className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 transform ${
                                                selectedHardware.length === 0
                                                ? 'bg-gray-600/50 text-gray-400 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white hover:scale-105 shadow-2xl hover:shadow-green-500/25'
                                            }`}
                                        >
                                            {isSubmitting ? (
                                                <span className="flex items-center justify-center">
                                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                                    Submitting Donation...
                                                </span>
                                            ) : (
                                                <span className="flex items-center justify-center">
                                                    <i className="fas fa-heart mr-2 text-red-400"></i>
                                                    Complete Donation
                                                </span>
                                            )}
                                        </button>                                        {selectedHardware.length === 0 && (
                                            <p className="text-center text-gray-400 text-sm">
                                                Please select hardware to continue
                                            </p>
                                        )}
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* SEO-Optimized Information Section for Organic Discovery */}
                        <div className="mt-16 max-w-6xl mx-auto">
                            <div className="bg-black/30 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {/* Why Donate Hardware */}
                                    <div className="space-y-4">
                                        <h3 className="text-xl font-semibold text-white">Why Donate Computer Hardware?</h3>
                                        <p className="text-gray-300 text-sm leading-relaxed">
                                            Donating your old computers, laptops, tablets, and smartphones creates immediate impact. 
                                            Every donated device provides digital access to students, job seekers, and families who 
                                            cannot afford new technology. Your hardware donation helps bridge the digital divide in the UK and globally.
                                        </p>
                                        <ul className="text-gray-400 text-sm space-y-1">
                                            <li>• Free laptop donations to students</li>
                                            <li>• Computer recycling with purpose</li>
                                            <li>• Educational technology access</li>
                                            <li>• Environmental sustainability</li>
                                        </ul>
                                    </div>

                                    {/* What We Accept */}
                                    <div className="space-y-4">
                                        <h3 className="text-xl font-semibold text-white">Hardware We Accept</h3>
                                        <p className="text-gray-300 text-sm leading-relaxed">
                                            We accept all types of working or repairable technology hardware. Our professional 
                                            refurbishment process ensures maximum utility from every donation. No device is too old - 
                                            we find the right use for every piece of technology.
                                        </p>
                                        <div className="grid grid-cols-2 gap-2 text-gray-400 text-sm">
                                            <div>• Desktop computers</div>
                                            <div>• Laptop computers</div>
                                            <div>• Tablet devices</div>
                                            <div>• Smartphones</div>
                                            <div>• Computer components</div>
                                            <div>• Network equipment</div>
                                        </div>
                                    </div>

                                    {/* Process & Impact */}
                                    <div className="space-y-4">
                                        <h3 className="text-xl font-semibold text-white">Free Collection & Impact</h3>
                                        <p className="text-gray-300 text-sm leading-relaxed">
                                            Our free collection service operates across the UK. Professional data wiping ensures 
                                            your privacy while our refurbishment process maximizes the lifespan of donated hardware. 
                                            Track your environmental and social impact through our transparent reporting system.
                                        </p>
                                        <div className="space-y-2 text-gray-400 text-sm">
                                            <div>✓ Free UK-wide collection</div>
                                            <div>✓ Professional data destruction</div>
                                            <div>✓ Certified refurbishment process</div>
                                            <div>✓ Impact tracking & reporting</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Call-to-Action Keywords */}                                <div className="mt-8 pt-8 border-t border-white/10 text-center">
                                    <p className="text-gray-400 text-sm">
                                        <strong className="text-white">Ready to donate?</strong> Join thousands of UK donors who have already contributed to bridging the digital divide. 
                                        Computer donation, laptop recycling, and hardware reuse programs that create real educational opportunities.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SEO Content Section */}
                    <section className="mt-16 mb-12">
                        <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
                            <h2 className="text-2xl font-bold text-white mb-6 text-center">
                                Why Choose Our Free Computer Recycling Service?
                            </h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <i className="fas fa-recycle text-green-400 text-2xl"></i>
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Zero Cost to You</h3>
                                    <p className="text-gray-300 text-sm">
                                        Free collection, free data destruction, free certificates. 
                                        No hidden charges ever.
                                    </p>
                                </div>
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <i className="fas fa-shield-alt text-blue-400 text-2xl"></i>
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Military-Grade Data Wiping</h3>
                                    <p className="text-gray-300 text-sm">
                                        DoD 5220.22-M standard data destruction with certificates provided for every device.
                                    </p>
                                </div>
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <i className="fas fa-leaf text-purple-400 text-2xl"></i>
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Environmental Impact</h3>
                                    <p className="text-gray-300 text-sm">
                                        Track your carbon savings. Each laptop recycled saves 80kg of CO₂ emissions.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Local SEO Section */}
                    <section className="mt-12 mb-16">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                Computer Recycling Services Across the UK
                            </h2>
                            <div className="flex flex-wrap justify-center gap-3 text-sm">
                                {['London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 'Liverpool', 
                                 'Bristol', 'Sheffield', 'Edinburgh', 'Cardiff', 'Belfast', 'Newcastle'].map(city => (
                                    <span key={city} className="bg-white/10 backdrop-blur-xl rounded-full px-4 py-2 text-gray-300">
                                        Computer Recycling {city}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Enhanced SEO Schema for Rich Snippets */}
                    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "NonprofitOrganization",
                                "@id": "https://sustainablecomputenetwork.org/#organization",
                                "name": "Sustainable Computer Network",
                                "url": "https://sustainablecomputenetwork.org",
                                "logo": "https://sustainablecomputenetwork.org/logo.png",
                                "description": "UK's leading computer recycling charity - Free collection, secure data destruction, environmental impact tracking",
                                "address": {
                                    "@type": "PostalAddress",
                                    "addressCountry": "GB"
                                },
                                "areaServed": "GB",
                                "nonprofitStatus": "Charity",
                                "keywords": "computer recycling, laptop donation, PC recycling, free computer collection, data destruction, e-waste recycling",
                                "sameAs": [
                                    "https://twitter.com/SustainableComp",
                                    "https://www.facebook.com/SustainableComputerNetwork",
                                    "https://www.linkedin.com/company/sustainable-computer-network"
                                ]
                            },
                            {
                                "@type": "Service",
                                "@id": "https://sustainablecomputenetwork.org/#recycling-service",
                                "name": "Free Computer Recycling & Collection Service",
                                "provider": {
                                    "@id": "https://sustainablecomputenetwork.org/#organization"
                                },
                                "description": "Free nationwide collection of laptops, desktops, servers. Secure data destruction included. Same-day collection available.",
                                "areaServed": {
                                    "@type": "Country",
                                    "name": "United Kingdom"
                                },
                                "availableChannel": {
                                    "@type": "ServiceChannel",
                                    "serviceUrl": "https://sustainablecomputenetwork.org/donate",
                                    "servicePhone": "+44-800-123-4567",
                                    "availableLanguage": "English"
                                },
                                "offers": {
                                    "@type": "Offer",
                                    "price": "0",
                                    "priceCurrency": "GBP",
                                    "eligibleRegion": "GB",
                                    "availability": "https://schema.org/InStock"
                                },
                                "aggregateRating": {
                                    "@type": "AggregateRating",
                                    "ratingValue": "4.9",
                                    "reviewCount": "2847",
                                    "bestRating": "5"
                                }
                            },
                            {
                                "@type": "FAQPage",
                                "mainEntity": [
                                    {
                                        "@type": "Question",
                                        "name": "How do I recycle my old computer for free?",
                                        "acceptedAnswer": {
                                            "@type": "Answer",
                                            "text": "Simply fill out our form, select your devices, and we'll arrange free collection within 24-48 hours. We handle everything including secure data destruction."
                                        }
                                    },
                                    {
                                        "@type": "Question",
                                        "name": "Is computer recycling really free?",
                                        "acceptedAnswer": {
                                            "@type": "Answer",
                                            "text": "Yes, completely free. No hidden charges. We cover collection, data destruction, and provide you with environmental impact certificates."
                                        }
                                    },
                                    {
                                        "@type": "Question",
                                        "name": "What happens to my recycled laptop?",
                                        "acceptedAnswer": {
                                            "@type": "Answer",
                                            "text": "After secure data wiping, working devices are refurbished and donated to schools and charities. Non-working devices are recycled responsibly."
                                        }
                                    }
                                ]
                            },
                            {
                                "@type": "HowTo",
                                "name": "How to Recycle Your Computer",
                                "step": [
                                    {
                                        "@type": "HowToStep",
                                        "name": "Select Your Devices",
                                        "text": "Choose from laptops, desktops, servers, tablets, or smartphones"
                                    },
                                    {
                                        "@type": "HowToStep",
                                        "name": "Fill Contact Details",
                                        "text": "Provide your collection address and preferred time"
                                    },
                                    {
                                        "@type": "HowToStep",
                                        "name": "Free Collection",
                                        "text": "We collect your devices for free within 24-48 hours"
                                    }
                                ]
                            }
                        ]
                    })}} />

                    {/* Floating Impact Counter */}
                    <div className="fixed bottom-8 left-8 z-40 hidden lg:block">
                        <div className="bg-black/80 backdrop-blur-xl rounded-2xl p-4 border border-green-500/30 shadow-2xl">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-green-400 mb-1">
                                    {Math.floor(Date.now() / 1000) % 1000 + 15000}
                                </div>
                                <div className="text-xs text-gray-400">Devices Recycled</div>
                            </div>
                        </div>
                    </div>

                    {/* Floating WhatsApp Button */}
                    <a href="https://wa.me/44XXXXXXXXXX?text=Hi,%20I%20want%20to%20recycle%20my%20computer" 
                       className="fixed bottom-8 right-8 z-40 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transition-all hover:scale-110"
                       target="_blank"
                       rel="noopener noreferrer">
                        <i className="fab fa-whatsapp text-2xl"></i>
                    </a>
                </div>
            </div>
        );
    } catch (error) {
        console.error('PremiumDonationPage error:', error);
        reportError(error);
        return null;
    }
}

export default PremiumDonationPage;
